import React from 'react';
import { Grid } from '@mui/material';
import { TableCard } from 'components/registry/components/TableCard';
import { CountryType } from 'components/registry/components/CountryType/CountryType';
import { PaginationResponse, ProducerVendorTypes } from 'components/registry/types/api';
import { useUserDataContext } from 'components/UserDataContext/context';
import { useGetPermission } from 'utils/hooks/useGetPermission';
import { ROLES } from 'utils/consts/roles';
import { ErulRegistrationCodes } from 'api/erul/types';
import { CerberusIntegrationStatus } from 'components/CerberusIntegrationStatus';
import { CerberusIntegrationError } from 'components/CerberusIntegrationError';
import { FeedDto } from '../../../api/params';
import {
  formatFeedAdditiveTypeList,
  formatProducer,
  formatVendor,
  getRegistryHeaderColor,
} from '../../../../utils';

type RegistryTableCardsWrapperProps = {
  data: PaginationResponse<FeedDto>;
  handleDelete: (id: string) => void;
};

export const RegistryTableCardsWrapper = ({
  data,
  handleDelete,
}: RegistryTableCardsWrapperProps) => {
  const { userData } = useUserDataContext();
  const permissions = {
    duplicate: useGetPermission(ROLES.feedAdditiveCard.duplicate, userData, true),
    edit: useGetPermission(ROLES.feedAdditiveCard.edit, userData, true),
    remove: useGetPermission(ROLES.feedAdditiveCard.remove, userData, true),
  };
  return (
    <Grid container spacing={2} mb={2}>
      {data.itemList.map((el) => (
        <TableCard
          key={el.id}
          title={`Запись ${el.systemIdNumber}`}
          subTitle={el.product?.name}
          subTitleTooltip="Наименование добавки"
          color={getRegistryHeaderColor(el.registryStatus.code)}
          id={el.id}
          addLink="/feed/addRequest"
          viewLink={`/registry/feed/registry/${el.id}`}
          onDelete={handleDelete}
          withEdit={
            permissions.edit &&
            el.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_NEW &&
            el.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_IN_PROGRESS
          }
          withDuplicate={permissions.duplicate}
          withRemove={permissions.remove}
        >
          <Grid container spacing={1} alignItems="baseline">
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Разработчик</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <div>
                <CountryType isResident={el.product.vendorType === ProducerVendorTypes.DOMESTIC} />
                <span>{formatVendor(el.product, true)}</span>
                <CerberusIntegrationStatus
                  isEnteredManually={el.product.vendorEnteredManually}
                  isNotLinkedWithCerberus={el.product.vendorNotLinkedWithCerberus}
                />
                <CerberusIntegrationError showMessage={el.product.vendorIncorrectCerberusLink} />
              </div>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Производитель</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              {el.product.producerList &&
                el.product.producerList.map((producer) => (
                  <div key={producer.id}>
                    <CountryType isResident={producer.resident} />
                    <span>{formatProducer(producer, true)}</span>
                    <CerberusIntegrationStatus
                      isEnteredManually={producer.enteredManually}
                      isNotLinkedWithCerberus={producer.notLinkedWithCerberus}
                    />
                    <CerberusIntegrationError showMessage={producer.incorrectCerberusLink} />
                  </div>
                ))}
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Форма</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.product.form}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Назначение</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.product.purpose}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Состав</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.product?.composition}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Тип</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{formatFeedAdditiveTypeList(el.product.feedAdditiveTypeList)}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Регистрационный номер</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.regNumber}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Дата регистрации</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.startValidityDate}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Срок действия</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.endValidityDate || 'бессрочно'}</p>
            </Grid>
            <Grid item xs={4} sm={3} className="cardLabel">
              <b>Статус</b>
            </Grid>
            <Grid item xs={8} sm={9}>
              <p>{el.registryStatus?.name}</p>
            </Grid>
            {userData && (
              <>
                <Grid item xs={4} sm={3} className="cardLabel">
                  <b>Регистрационный номер ЕРУЛ</b>
                </Grid>
                <Grid item xs={8} sm={9}>
                  <p>{`${el.erulRegNumber || ''} ${
                    el.erulRegDate ? `(${el.erulRegDate})` : ''
                  }`}</p>
                </Grid>
              </>
            )}
          </Grid>
        </TableCard>
      ))}
    </Grid>
  );
};
